/* global firebase */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import Button from '../../../common/button/Button';
import Spinner from '../../../common/Spinner/Spinner';
import { showError } from '../../../common/A1Alert/A1Alert';

// Module-level Promise shared across all FirebaseAuthComponent instances.
// Ensures the Firebase scripts are only ever appended to the DOM once,
// even when Login and Register components are both mounted at the same time.
// All instances await the same Promise and update their own state when it resolves.
let firebaseScriptsPromise = null;

function loadFirebaseScripts() {
    if (firebaseScriptsPromise) return firebaseScriptsPromise;

    firebaseScriptsPromise = new Promise((resolve) => {
        if (window.firebase !== undefined) {
            resolve();

            return;
        }

        // Using google CDN to load the scripts to avoid bloating the bundle
        // and also because only a few people will actually need this scripts
        // Firebase App (the core Firebase SDK) is always required and must be listed first
        const baseScript = document.createElement('script');

        baseScript.src = 'https://www.gstatic.com/firebasejs/12.13.0/firebase-app-compat.js'; // @TODO use the module imports one day (instead of the compat version)

        baseScript.onload = () => {
            // Add Firebase Auth only once the base script has loaded to guarantee order
            const authScript = document.createElement('script');

            authScript.src = 'https://www.gstatic.com/firebasejs/12.13.0/firebase-auth-compat.js'; // @TODO use the module imports one day (instead of the compat version)

            authScript.onload = () => resolve();

            document.body.appendChild(authScript);
        };

        document.body.appendChild(baseScript);
    });

    return firebaseScriptsPromise;
}

export class FirebaseAuthComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            firebaseSubmitting: false,
            firebaseBaseScriptLoaded: window && window.firebase !== undefined,
            firebaseAuthScriptLoaded: window && window.firebase !== undefined,
        };
    }

    componentDidMount() {
        if (window.firebase === undefined) {
            // load the firebase scripts after a minimal delay to not trouble the modal animation
            this._loadScriptTimeout = setTimeout(() => {
                loadFirebaseScripts().then(() => {
                    if (!this._unmounted) {
                        this.setState({
                            firebaseBaseScriptLoaded: true,
                            firebaseAuthScriptLoaded: true,
                        });
                    }
                });
            }, 700);
        } else {
            this.setState({
                firebaseBaseScriptLoaded: true,
                firebaseAuthScriptLoaded: true,
            });
        }
    }

    componentWillUnmount() {
        this._unmounted = true;
        clearTimeout(this._loadScriptTimeout);
    }

    _firebaseAuth = (signInProvider) => {
        if (this.state.firebaseSubmitting || this.props.submitting === 'login' || this.props.submitting === 'registerUser') {
            // Note: This might cause wrong error messages if there have been problems with the normal login or register function and
            // the user tries to use a social login... (Too lazy to properly treat this case as it will probably not happen very often)
            return showError(`A autenticação já foi iniciada através de ${this.state.firebaseSubmitting}. Por favor, finalize o processo na outra janela.`);
        }

        if (!this.state.firebaseBaseScriptLoaded || !this.state.firebaseAuthScriptLoaded) {
            // Scripts not yet loaded (not sure if this will ever happen as there shouldn't be any buttons to press if the scripts have not loaded)
            return showError('Por favor, aguarde a página carregar os recursos necessários.');
        }

        if (window.firebase === undefined) {
            // This should never happen, because of the checks above...
            // eslint-disable-next-line no-console
            console.error('no firebase object found!');

            return showError('Desculpe, algo deu errado! Tente recarregar a página e se o erro persiste entre em contato conosco.');
        }

        let firebaseConfig;

        if (process.env.NODE_ENV === 'production') {
            // Production firebase config
            firebaseConfig = {
                apiKey: 'AIzaSyDYjlJB1-1JQdQMSi8wDkWdjGb8r-IK8qQ',
                authDomain: 'auth.antena1.com.br',
                databaseURL: 'https://antena-1-app.firebaseio.com',
                projectId: 'antena-1-app',
                storageBucket: 'antena-1-app.appspot.com',
                messagingSenderId: '598772598473',
                appId: '1:598772598473:web:47adb60c768c8ab2016f18',
                measurementId: 'G-VHNGCK8KKX',
            };
        } else {
            // Dev firebase config
            firebaseConfig = {
                apiKey: 'AIzaSyDZp0O52XDDLCrhfK0Q4S_WTsTjDgG0PyU',
                authDomain: 'authdev.antena1.com.br',
                databaseURL: 'https://antena-1-app-staging.firebaseio.com',
                projectId: 'antena-1-app-staging',
                storageBucket: 'antena-1-app-staging.appspot.com',
                messagingSenderId: '280693700630',
                appId: '1:280693700630:web:546750800e55e3ca62dd37',
                measurementId: 'G-F4726EV2QD',
            };
        }

        this.setState({
            firebaseSubmitting: signInProvider,
        });

        this.props.setFirebasePopupOpen(true);

        if (firebase.apps.length === 0) {
            // Initialize Firebase
            firebase.initializeApp(firebaseConfig);
        }

        let provider;

        if (signInProvider === 'google.com') {
            provider = new firebase.auth.GoogleAuthProvider();
        } else if (signInProvider === 'facebook.com') {
            provider = new firebase.auth.FacebookAuthProvider();
        } else if (signInProvider === 'apple.com') {
            provider = new firebase.auth.OAuthProvider('apple.com');
        } else {
            // eslint-disable-next-line no-console
            return console.error('unsupported signInProvider', signInProvider);
        }

        // optional (check https://developers.google.com/identity/protocols/oauth2/scopes)
        // provider.addScope('https://www.googleapis.com/auth/contacts.readonly');

        // To apply the default browser preference instead of explicitly setting it.
        firebase.auth().useDeviceLanguage();

        // optional
        // provider.setCustomParameters({
        //     login_hint: 'user@example.com',
        // });

        // Notes:
        // I could not implement the redirect method for authentication as recomended by the google docs (for example: https://firebase.google.com/docs/auth/web/google-signin)
        // This is because I would have to add some flag in the url to open the Profile modal and to check for the callback and stuff.

        return firebase.auth().signInWithPopup(provider)
            .then((result) => {
                // This gives you a Google Access Token. You can use it to access the Google API.
                // const token = result.credential.accessToken;

                // The signed-in user info.
                const user = result.user;

                if (!user.email) {
                    return showError(`É necessário ter um email cadastrado e verificado em ${signInProvider} para acessar a página da Antena 1.`);
                }

                // Note: facebooks users emails are never verified. There is some discussion about this here: https://github.com/firebase/firebase-js-sdk/issues/340
                // Some say, if facebook returns an email, it is already verified. I will believe this for now...
                // This must be checked for every new provider individually
                if (signInProvider !== 'facebook.com' && !user.emailVerified) {
                    return showError(`É necessário ter um email cadastrado e verificado em ${signInProvider} para acessar a página da Antena 1.`);
                }

                return user.getIdToken().then((idToken) => {
                    if (this.props.page === 'signup') {
                        return this.props.registerUserFirebase(idToken, signInProvider);
                    }

                    return this.props.loginFirebase(idToken, signInProvider);
                }).catch((error) => {
                    // eslint-disable-next-line no-console
                    console.error('something wrong with the idToken', error);

                    return showError('Desculpe, algo deu errado! Tente novamente em alguns minutos ou utilize outra forma de autenticação.');
                });
            })
            .catch((error) => {
                // Handle Errors here.
                // const errorCode = error.code;
                // const errorMessage = error.message;
                // The email of the user's account used.
                // const email = error.email;
                // The firebase.auth.AuthCredential type that was used.
                // const credential = error.credential;

                // eslint-disable-next-line no-console
                console.error('Error from firebase auth:', error);

                if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
                    // auth/popup-closed-by-user: user deliberately closed the popup — no error needed.
                    // auth/cancelled-popup-request: a second popup was opened before this one finished — the
                    // second popup will handle the result, so this one can be silently discarded.
                    // Both are expected user actions, not errors worth surfacing.
                    return null;
                }

                if (error.code === 'auth/account-exists-with-different-credential') {
                    // this user has already an account linked to a differen signInProvider
                    if (this.props.page === 'signup') {
                        return showError('Já existe um cadastro com esse email utilizando um outro meio de autenticação. Por favor, efetue o login com o outro meio de autenticação ou preencha os campos obrigatórios.');
                    }

                    return showError('Login não efetuado! Esse email é atrelado a um outro meio de autenticação. Por favor, utilize o outro meio de autenticação ou email e senha para se logar.');
                }

                return showError('Desculpe, algo deu errado! Tente novamente em alguns minutos ou utilize outra forma de autenticação.');
            })
            .finally(() => {
                this.setState({
                    firebaseSubmitting: false,
                });

                this.props.setFirebasePopupOpen(false);
            });
    }


    render() {
        const firebaseScriptsLoaded = this.state.firebaseBaseScriptLoaded && this.state.firebaseAuthScriptLoaded;

        return (
            <div className="containerFirebaseAuth">
                {/* <div className="containerTitle">
                    <p className="ouText">
                        OU
                    </p>
                </div> */}
                {
                    !firebaseScriptsLoaded ?
                        <Spinner />
                        :
                        <React.Fragment>
                            <div className="containerLoginButton">
                                <Button className={this.state.firebaseSubmitting === 'google.com' ? 'loading' : ''} title="continuar com Google" onClick={() => this._firebaseAuth('google.com')}>
                                    <span>
                                        <img className="icon" alt="Icon Google" src="/social_login_google.png" />
                                        Faça login com o Google
                                    </span>
                                </Button>
                            </div>
                            {/* <div className="containerLoginButton">
                                <Button className={this.state.firebaseSubmitting === 'apple.com' ? 'loading' : ''} title="continuar com Apple" onClick={() => this._firebaseAuth('apple.com')}>
                                    <span>
                                        <img className="icon" alt="Icon Apple" src="/social_login_apple.png" />
                                        Faça login com a Apple
                                    </span>
                                </Button>
                            </div> */}
                            <div className="containerLoginButton">
                                <Button className={this.state.firebaseSubmitting === 'facebook.com' ? 'loading' : ''} title="continuar com facebook" onClick={() => this._firebaseAuth('facebook.com')}>
                                    <span>
                                        <img className="icon" alt="Icon Facebook" src="/social_login_facebook.png" />
                                        Faça login com o Facebook
                                    </span>
                                </Button>
                            </div>
                        </React.Fragment>
                }
            </div>
        );
    }
}

FirebaseAuthComponent.propTypes = exact({
    page: PropTypes.string.isRequired,
    loginFirebase: PropTypes.func.isRequired,
    registerUserFirebase: PropTypes.func.isRequired,
    setFirebasePopupOpen: PropTypes.func.isRequired,
    submitting: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string,
    ]).isRequired,
});

export default FirebaseAuthComponent;
